import { Player, Position } from '../models/types';
import { evaluatePlayerContractAcceptance } from './simulation';

export type PortfolioDiscoveryStatus = 'PENDING' | 'DISCOVERED';

export interface PortfolioItem {
  id?: string;
  playerId: string;
  playerInstanceId: string;
  playerDefinitionId?: string;
  name?: string;
  displayName?: string;
  position?: Position | string;
  age?: number;
  overall?: number;
  averagePoints?: number | null;
  last3AveragePoints?: number | null;
  type?: string;
  marketValue?: number;
  currentMarketValue?: number;
  expectedVersion?: number;
  realClub?: {
    id?: string;
    name?: string;
    shortName?: string;
  };
  unavailabilities?: any[];
  ownerClubId?: string | null;
  sellerClubId?: string | null;
  clubId?: string | null;
  clubName?: string;
  clubCode?: string;
  crestUrl?: string | null;
  revealed?: boolean;
  releaseClause?: number | null;
  status: PortfolioDiscoveryStatus;
  discoveryStatus?: PortfolioDiscoveryStatus;
  discoveredClause: number | null; // en euros (€)
  lastKnownClause?: number | null;
  addedAt: string;
  discoveredAt?: string | null;
  player?: {
    id: string;
    playerDefinitionId?: string;
    playerInstanceId?: string;
    name: string;
    position: Position | string;
    age?: number;
    clubId: string | null;
    clubName?: string;
    crestUrl?: string | null;
    marketValue?: number;
    overall?: number;
    currentClause?: number | null;
  };
}

export interface PortfolioResponse {
  clubId: string;
  items: PortfolioItem[];
  count: number;
  maxLimit: number; // 3
}

export interface ReleaseClauseAttemptPayload {
  playerId: string;
  clauseAmount?: number; // importe del traspaso correspondiente a la cláusula descubierta (en €)
  discoveredClause?: number;
  annualSalary: number; // salario anual ofrecido (en €)
  termSeasons?: number; // temporadas
  contractDurationYears?: number;
  releaseClause?: number; // nueva cláusula en el club comprador (en €)
  newReleaseClause?: number;
}

export interface ReleaseClauseAttemptResponse {
  id: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  type: 'RELEASE_CLAUSE';
  playerId: string;
  playerName?: string;
  playerPosition?: string;
  buyerClubId: string;
  sellerClubId: string;
  sellerClubName?: string;
  clauseAmount: number; // en €
  terms: {
    annualSalary: number;
    termSeasons: number;
    releaseClause: number;
  };
  submittedAt: string;
  resolutionAt?: string;
  rejectionReason?: string;
}

export interface ContractRenewalPayload {
  playerId: string;
  annualSalary: number; // en €
  termSeasons?: number; // temporadas
  contractDurationYears?: number;
  releaseClause?: number; // en €
  newReleaseClause?: number;
}

export interface ContractRenewalResponse {
  id: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  type: 'CONTRACT_RENEWAL';
  playerId: string;
  playerName?: string;
  playerPosition?: string;
  clubId: string;
  previousContract?: {
    salary: number; // en €
    durationYears: number;
    releaseClause: number; // en €
  };
  offeredContract: {
    annualSalary: number; // en €
    termSeasons: number;
    releaseClause: number; // en €
  };
  submittedAt: string;
  resolutionAt?: string;
  rejectionReason?: string;
}

const STORAGE_PREFIX = 'lom_market_v2';

function getStorageKey(clubId: string, sub: string): string {
  return `${STORAGE_PREFIX}_${clubId}_${sub}`;
}

function readStorage<T>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage<T>(key: string, value: T): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

const simulatedPlayerRegistry = new Map<string, Player>();

export const portfolioSimulation = {
  /**
   * Registra un jugador en el registro interno de simulación.
   */
  registerPlayer(player: Player): void {
    if (player && player.id) {
      simulatedPlayerRegistry.set(player.id, player);
      if (player.playerInstanceId) {
        simulatedPlayerRegistry.set(player.playerInstanceId, player);
      }
    }
  },

  getRegisteredPlayer(playerId: string): Player | undefined {
    return simulatedPlayerRegistry.get(playerId);
  },

  /**
   * Obtiene la cartera de un club, sincronizando el estado con los contratos actuales.
   */
  getPortfolio(clubId: string, playerFinder?: (id: string) => Player | undefined): PortfolioResponse {
    const key = getStorageKey(clubId, 'portfolio');
    const items = readStorage<PortfolioItem[]>(key, []);

    let modified = false;
    const synchronizedItems = items.map((item) => {
      const player = playerFinder ? playerFinder(item.playerId) : simulatedPlayerRegistry.get(item.playerId);
      if (!player) return item;

      const currentClauseEur = Math.round((player.contract?.releaseClause ?? 0) * 1_000_000);
      let updatedStatus = item.status;
      let updatedDiscoveredClause = item.discoveredClause;

      // Criterio 6: Si cambia la cláusula, vuelve a estar pendiente hasta un nuevo cierre.
      if (item.status === 'DISCOVERED' && item.discoveredClause !== null) {
        if (item.discoveredClause !== currentClauseEur) {
          updatedStatus = 'PENDING';
          updatedDiscoveredClause = null;
          modified = true;
        }
      }

      const crestCode = player.realClub?.shortName || (player.clubId ? player.clubId.slice(0, 3).toUpperCase() : null);
      const crestUrl = crestCode ? `crest/${crestCode}.png` : null;

      return {
        ...item,
        id: player.playerInstanceId || player.id,
        name: player.name,
        position: player.position,
        age: player.age,
        overall: player.overall,
        clubId: player.clubId,
        clubName: player.clubName || (player.clubId ? `Club ${player.clubId}` : 'Sin Club'),
        clubCode: player.realClub?.shortName || (player.clubId ? player.clubId.slice(0, 3).toUpperCase() : undefined),
        crestUrl,
        currentMarketValue: player.currentMarketValue ? Math.round(player.currentMarketValue * 1_000_000) : 0,
        status: updatedStatus,
        discoveryStatus: updatedStatus,
        discoveredClause: updatedDiscoveredClause,
        player: {
          id: player.id,
          playerDefinitionId: player.playerDefinitionId,
          playerInstanceId: player.playerInstanceId,
          name: player.name,
          position: player.position,
          age: player.age,
          clubId: player.clubId,
          clubName: player.clubName || (player.clubId ? `Club ${player.clubId}` : 'Sin Club'),
          crestUrl,
          marketValue: player.currentMarketValue,
          overall: player.overall,
          currentClause: currentClauseEur,
        },
      };
    });

    if (modified) {
      writeStorage(key, synchronizedItems);
    }

    return {
      clubId,
      items: synchronizedItems,
      count: synchronizedItems.length,
      maxLimit: 3,
    };
  },

  /**
   * Añade un jugador a la cartera del club respetando las reglas de negocio:
   * - Máximo 3 jugadores en cartera (Criterio 1).
   * - No propios ni libres (Criterio 2).
   * - Añadir lo deja como pendiente de descubrir (Criterio 3 y 5).
   */
  addToPortfolio(
    clubId: string,
    playerId: string,
    playerFinder: (id: string) => Player | undefined,
    userClubId: string
  ): PortfolioItem {
    let player = playerFinder(playerId) || simulatedPlayerRegistry.get(playerId);
    if (!player) {
      const err: any = new Error('Futbolista no encontrado.');
      err.status = 404;
      throw err;
    }

    // Registrar en memoria de simulación para consultas y resoluciones futuras
    simulatedPlayerRegistry.set(player.id, player);
    if (player.playerInstanceId) {
      simulatedPlayerRegistry.set(player.playerInstanceId, player);
    }

    // Criterio 2: No se permite añadir jugadores propios ni libres
    if (player.clubId === userClubId || player.clubId === clubId) {
      const err: any = new Error('No se permite añadir jugadores de tu propia plantilla a la cartera.');
      err.status = 400;
      err.code = 'CANNOT_ADD_OWN_PLAYER';
      throw err;
    }
    if (!player.clubId) {
      const err: any = new Error('No se permite añadir agentes libres a la cartera. Solo jugadores con contrato que pertenezcan a otro club.');
      err.status = 400;
      err.code = 'CANNOT_ADD_FREE_AGENT';
      throw err;
    }

    const key = getStorageKey(clubId, 'portfolio');
    const existing = readStorage<PortfolioItem[]>(key, []);

    // Si ya está en cartera, quitar y volver a añadir reinicia el descubrimiento a PENDING (Criterio 5)
    const existingIndex = existing.findIndex((i) => i.playerId === playerId);
    if (existingIndex >= 0) {
      existing.splice(existingIndex, 1);
    } else if (existing.length >= 3) {
      // Criterio 1: Con tres jugadores en cartera, no se permite añadir un cuarto y se explica el límite
      const err: any = new Error('Límite alcanzado: el club solo puede tener un máximo de 3 jugadores en cartera.');
      err.status = 400;
      err.code = 'PORTFOLIO_LIMIT_REACHED';
      throw err;
    }

    const crestCode = player.realClub?.shortName || (player.clubId ? player.clubId.slice(0, 3).toUpperCase() : null);
    const crestUrl = crestCode ? `crest/${crestCode}.png` : null;

    const newItem: PortfolioItem = {
      id: player.playerInstanceId || player.id,
      playerId: player.id,
      playerInstanceId: player.playerInstanceId,
      name: player.name,
      position: player.position,
      age: player.age,
      overall: player.overall,
      clubId: player.clubId,
      clubName: player.clubName || (player.clubId ? `Club ${player.clubId}` : 'Sin Club'),
      clubCode: crestCode || undefined,
      crestUrl,
      currentMarketValue: player.currentMarketValue ? Math.round(player.currentMarketValue * 1_000_000) : 0,
      status: 'PENDING',
      discoveryStatus: 'PENDING',
      discoveredClause: null,
      addedAt: new Date().toISOString(),
      discoveredAt: null,
      lastKnownClause: null,
      player: {
        id: player.id,
        playerDefinitionId: player.playerDefinitionId,
        playerInstanceId: player.playerInstanceId,
        name: player.name,
        position: player.position,
        age: player.age,
        clubId: player.clubId,
        clubName: player.clubName || `Club ${player.clubId}`,
        crestUrl,
        marketValue: player.currentMarketValue,
        overall: player.overall,
        currentClause: Math.round((player.contract?.releaseClause ?? 0) * 1_000_000),
      },
    };

    const updated = [...existing, newItem];
    writeStorage(key, updated);
    return newItem;
  },

  /**
   * Retira un jugador de la cartera.
   */
  removeFromPortfolio(clubId: string, playerId: string): { success: boolean; playerId: string } {
    const key = getStorageKey(clubId, 'portfolio');
    const existing = readStorage<PortfolioItem[]>(key, []);
    const updated = existing.filter((i) => i.playerId !== playerId);
    writeStorage(key, updated);
    return { success: true, playerId };
  },

  /**
   * Presenta un clausulazo sobre un jugador cuya cláusula ha sido descubierta.
   * Reglas:
   * - Solo se ofrece cuando la cláusula está descubierta (Criterio 7).
   * - Transfer amount corresponde a la cláusula descubierta.
   * - No hay prima de fichaje (0 €).
   * - Valida fondos suficientes sin borrar inputs (Criterio 11).
   * - La operación queda PENDIENTE (Criterio 8).
   * - El club propietario no ve la oferta (Criterio 9).
   */
  submitReleaseClauseAttempt(
    clubId: string,
    payload: ReleaseClauseAttemptPayload,
    playerFinder: (id: string) => Player | undefined,
    availableBalanceEur: number
  ): ReleaseClauseAttemptResponse {
    const portfolio = this.getPortfolio(clubId, playerFinder);
    const item = portfolio.items.find((i) => i.playerId === payload.playerId);

    if (!item || item.status !== 'DISCOVERED' || item.discoveredClause === null) {
      const err: any = new Error('Solo se puede presentar un clausulazo sobre jugadores cuya cláusula ya esté descubierta en cartera.');
      err.status = 400;
      err.code = 'CLAUSE_NOT_DISCOVERED';
      throw err;
    }

    const player = playerFinder(payload.playerId);
    if (!player) {
      const err: any = new Error('Futbolista no encontrado.');
      err.status = 404;
      throw err;
    }

    // Verificar si la cláusula real del contrato ha cambiado respecto a la descubierta
    const currentRealClauseEur = Math.round((player.contract?.releaseClause ?? 0) * 1_000_000);
    if (currentRealClauseEur !== item.discoveredClause) {
      // Revertir estado a PENDING en cartera
      this.getPortfolio(clubId, playerFinder);
      const err: any = new Error('La cláusula del jugador ha cambiado recientemente. Vuelve a estar pendiente de descubrir en el próximo cierre.');
      err.status = 409;
      err.code = 'CLAUSE_CHANGED';
      throw err;
    }

    const clauseAmount = payload.clauseAmount ?? payload.discoveredClause ?? 0;
    const annualSalary = payload.annualSalary;
    const termSeasons = payload.termSeasons ?? payload.contractDurationYears ?? 1;
    const releaseClause = payload.releaseClause ?? payload.newReleaseClause ?? 0;

    // Comprobar importe del traspaso vs cláusula descubierta
    if (Math.abs(clauseAmount - item.discoveredClause) > 1000) {
      const err: any = new Error(`El importe del traspaso (${(clauseAmount / 1_000_000).toFixed(2)} M€) debe coincidir exactamente con la cláusula descubierta (${(item.discoveredClause / 1_000_000).toFixed(2)} M€).`);
      err.status = 400;
      err.code = 'INVALID_CLAUSE_AMOUNT';
      throw err;
    }

    // Validación de fondos suficientes (Cláusula de traspaso)
    const totalRequiredEur = clauseAmount;
    if (totalRequiredEur > availableBalanceEur) {
      const err: any = new Error(`Fondos insuficientes: dispones de ${(availableBalanceEur / 1_000_000).toFixed(2)} M€ y pagar la cláusula requiere ${(totalRequiredEur / 1_000_000).toFixed(2)} M€.`);
      err.status = 409;
      err.code = 'FUNDS_RESERVATION_REJECTED';
      err.failure = {
        code: 'FUNDS_RESERVATION_REJECTED',
        message: err.message,
        requestedAmount: totalRequiredEur,
        availableAmount: availableBalanceEur,
      };
      throw err;
    }

    const attemptsKey = getStorageKey(clubId, 'release_attempts');
    const existingAttempts = readStorage<ReleaseClauseAttemptResponse[]>(attemptsKey, []);

    const attempt: ReleaseClauseAttemptResponse = {
      id: `rca-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      status: 'PENDING',
      type: 'RELEASE_CLAUSE',
      playerId: player.id,
      playerName: player.name,
      playerPosition: player.position,
      buyerClubId: clubId,
      sellerClubId: player.clubId || 'unknown',
      sellerClubName: player.clubName,
      clauseAmount,
      terms: {
        annualSalary,
        termSeasons,
        releaseClause,
      },
      submittedAt: new Date().toISOString(),
    };

    writeStorage(attemptsKey, [attempt, ...existingAttempts]);
    return attempt;
  },

  /**
   * Obtiene los intentos de clausulazo activos o pendientes del club comprador.
   */
  getReleaseClauseAttempts(clubId: string): ReleaseClauseAttemptResponse[] {
    const attemptsKey = getStorageKey(clubId, 'release_attempts');
    return readStorage<ReleaseClauseAttemptResponse[]>(attemptsKey, []);
  },

  /**
   * Propone una renovación de contrato para un jugador de la propia plantilla.
   * Reglas:
   * - Solo jugadores propios.
   * - No lleva traspaso ni prima de fichaje (0 €).
   * - Queda PENDIENTE hasta el cierre diario (Criterio 8).
   */
  submitContractRenewal(
    clubId: string,
    payload: ContractRenewalPayload,
    playerFinder: (id: string) => Player | undefined
  ): ContractRenewalResponse {
    const player = playerFinder(payload.playerId);
    if (!player) {
      const err: any = new Error('Futbolista no encontrado.');
      err.status = 404;
      throw err;
    }

    if (player.clubId !== clubId) {
      const err: any = new Error('Solo puedes proponer renovaciones a futbolistas de tu propia plantilla.');
      err.status = 400;
      err.code = 'CANNOT_RENEW_OTHER_CLUB_PLAYER';
      throw err;
    }

    const annualSalary = payload.annualSalary;
    // En las renovaciones no hay temporadas, siempre será una
    const termSeasons = 1;
    const releaseClause = payload.releaseClause ?? payload.newReleaseClause ?? 0;

    if (annualSalary <= 0 || releaseClause <= 0) {
      const err: any = new Error('Las condiciones de salario y cláusula deben ser valores positivos.');
      err.status = 400;
      err.code = 'INVALID_CONTRACT_TERMS';
      throw err;
    }

    const renewalsKey = getStorageKey(clubId, 'contract_renewals');
    const existing = readStorage<ContractRenewalResponse[]>(renewalsKey, []);

    const prevSalaryEur = Math.round((player.contract?.salary ?? 0) * 1_000_000);
    const prevClauseEur = Math.round((player.contract?.releaseClause ?? 0) * 1_000_000);
    const prevDuration = player.contract?.durationYears ?? 1;

    const renewal: ContractRenewalResponse = {
      id: `ren-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      status: 'PENDING',
      type: 'CONTRACT_RENEWAL',
      playerId: player.id,
      playerName: player.name,
      playerPosition: player.position,
      clubId,
      previousContract: {
        salary: prevSalaryEur,
        durationYears: prevDuration,
        releaseClause: prevClauseEur,
      },
      offeredContract: {
        annualSalary: payload.annualSalary,
        termSeasons: 1,
        releaseClause: releaseClause,
      },
      submittedAt: new Date().toISOString(),
    };

    writeStorage(renewalsKey, [renewal, ...existing.filter((r) => r.playerId !== payload.playerId || r.status !== 'PENDING')]);
    return renewal;
  },

  /**
   * Obtiene las propuestas de renovación registradas para el club.
   */
  getContractRenewals(clubId: string): ContractRenewalResponse[] {
    const renewalsKey = getStorageKey(clubId, 'contract_renewals');
    return readStorage<ContractRenewalResponse[]>(renewalsKey, []);
  },

  /**
   * Resuelve el cierre diario del mercado para:
   * 1. Descubrir cláusulas pendientes en todas las carteras.
   * 2. Evaluar y resolver clausulazos vs renovaciones pendientes según las reglas oficiales.
   * 3. Actualizar contratos, plantillas y balances de tesorería (Criterio 10).
   */
  resolveDailyMarket(options: {
    userClubId: string;
    allPlayers: Player[];
    updatePlayer: (player: Player) => void;
    updateClubBalance: (clubId: string, deltaEur: number, concept: string, referenceId?: string) => void;
  }): {
    discoveredCount: number;
    resolvedAttemptsCount: number;
    resolvedRenewalsCount: number;
    summary: string[];
  } {
    const { userClubId, allPlayers, updatePlayer, updateClubBalance } = options;
    const summary: string[] = [];
    const playerMap = new Map<string, Player>(allPlayers.map((p) => [p.id, p]));

    // 1. Descubrir cláusulas pendientes en la cartera del usuario
    const portfolioKey = getStorageKey(userClubId, 'portfolio');
    const portfolio = readStorage<PortfolioItem[]>(portfolioKey, []);
    let discoveredCount = 0;

    const updatedPortfolio = portfolio.map((item) => {
      const p = playerMap.get(item.playerId);
      if (!p) return item;

      const currentClauseEur = Math.round((p.contract?.releaseClause ?? 0) * 1_000_000);

      // Criterio 4: Tras el cierre diario, se muestra la cláusula descubierta.
      if (item.status === 'PENDING') {
        discoveredCount++;
        summary.push(`Cláusula descubierta: ${p.name} tiene una cláusula de ${(currentClauseEur / 1_000_000).toFixed(1)} M€.`);
        return {
          ...item,
          status: 'DISCOVERED' as PortfolioDiscoveryStatus,
          discoveredClause: currentClauseEur,
          lastKnownClause: currentClauseEur,
          discoveredAt: new Date().toISOString(),
        };
      } else if (item.status === 'DISCOVERED') {
        // Criterio 6: Si la cláusula ha cambiado, vuelve a estar pendiente hasta un nuevo cierre
        if (item.discoveredClause !== null && item.discoveredClause !== currentClauseEur) {
          return {
            ...item,
            status: 'PENDING' as PortfolioDiscoveryStatus,
            discoveredClause: null,
            lastKnownClause: currentClauseEur,
          };
        }
      }
      return item;
    });

    writeStorage(portfolioKey, updatedPortfolio);

    // 2. Cargar clausulazos pendientes y renovaciones pendientes
    const attemptsKey = getStorageKey(userClubId, 'release_attempts');
    const attempts = readStorage<ReleaseClauseAttemptResponse[]>(attemptsKey, []);
    const pendingAttempts = attempts.filter((a) => a.status === 'PENDING');

    const renewalsKey = getStorageKey(userClubId, 'contract_renewals');
    const renewals = readStorage<ContractRenewalResponse[]>(renewalsKey, []);
    const pendingRenewals = renewals.filter((r) => r.status === 'PENDING');

    let resolvedAttemptsCount = 0;
    let resolvedRenewalsCount = 0;

    // Procesar cada jugador involucrado
    const involvedPlayerIds = new Set<string>([
      ...pendingAttempts.map((a) => a.playerId),
      ...pendingRenewals.map((r) => r.playerId),
    ]);

    for (const playerId of involvedPlayerIds) {
      const player = playerMap.get(playerId);
      if (!player) continue;

      const playerAttempts = pendingAttempts.filter((a) => a.playerId === playerId);
      const playerRenewal = pendingRenewals.find((r) => r.playerId === playerId);

      // CASO A: Hay propuesta de renovación Y uno o más clausulazos
      if (playerRenewal && playerAttempts.length > 0) {
        // Evaluar score de la renovación
        const renewalSalaryM = playerRenewal.offeredContract.annualSalary / 1_000_000;
        const renewalClauseM = playerRenewal.offeredContract.releaseClause / 1_000_000;
        const renewalDuration = playerRenewal.offeredContract.termSeasons;
        const renewalEval = evaluatePlayerContractAcceptance(
          player,
          renewalSalaryM,
          renewalDuration,
          renewalClauseM,
          'Primera División',
          0
        );

        // Evaluar score del mejor clausulazo
        let bestAttempt = playerAttempts[0];
        let bestAttemptEval = evaluatePlayerContractAcceptance(
          player,
          bestAttempt.terms.annualSalary / 1_000_000,
          bestAttempt.terms.termSeasons,
          bestAttempt.terms.releaseClause / 1_000_000,
          'Primera División',
          0
        );

        for (let i = 1; i < playerAttempts.length; i++) {
          const curAttempt = playerAttempts[i];
          const curEval = evaluatePlayerContractAcceptance(
            player,
            curAttempt.terms.annualSalary / 1_000_000,
            curAttempt.terms.termSeasons,
            curAttempt.terms.releaseClause / 1_000_000,
            'Primera División',
            0
          );
          if (curEval.score > bestAttemptEval.score) {
            bestAttempt = curAttempt;
            bestAttemptEval = curEval;
          }
        }

        // Regla: "Si hay clausulazos pendientes, el jugador compara la renovación con esas ofertas usando las reglas actuales. En caso de empate, acepta la renovación y permanece en su club."
        if (renewalEval.score >= bestAttemptEval.score) {
          // ACEPTA RENOVACIÓN
          playerRenewal.status = 'ACCEPTED';
          playerRenewal.resolutionAt = new Date().toISOString();
          resolvedRenewalsCount++;

          // Actualizar contrato del jugador en plantilla
          const updatedPlayer: Player = {
            ...player,
            contract: {
              ...player.contract,
              salary: renewalSalaryM,
              durationYears: renewalDuration,
              releaseClause: renewalClauseM,
              startSeason: 1,
              endSeason: 1 + renewalDuration,
            },
          };
          updatePlayer(updatedPlayer);
          playerMap.set(player.id, updatedPlayer);

          summary.push(`¡Renovación completada! ${player.name} aceptó la renovación y rechazó el clausulazo.`);

          // Rechazar todos los clausulazos
          for (const att of playerAttempts) {
            att.status = 'REJECTED';
            att.resolutionAt = new Date().toISOString();
            att.rejectionReason = 'El jugador prefirió renovar y continuar en su club actual.';
            resolvedAttemptsCount++;
          }
        } else if (bestAttemptEval.score >= 70) {
          // ACEPTA CLAUSULAZO
          bestAttempt.status = 'ACCEPTED';
          bestAttempt.resolutionAt = new Date().toISOString();
          resolvedAttemptsCount++;

          // Rechazar renovación
          playerRenewal.status = 'REJECTED';
          playerRenewal.resolutionAt = new Date().toISOString();
          playerRenewal.rejectionReason = 'El jugador aceptó la oferta del clausulazo.';
          resolvedRenewalsCount++;

          // Rechazar otros clausulazos
          for (const att of playerAttempts) {
            if (att.id !== bestAttempt.id) {
              att.status = 'REJECTED';
              att.resolutionAt = new Date().toISOString();
              att.rejectionReason = 'Superado por otra oferta de clausulazo.';
              resolvedAttemptsCount++;
            }
          }

          // Ejecutar traspaso
          const transferCostEur = bestAttempt.clauseAmount;
          const buyerSalaryM = bestAttempt.terms.annualSalary / 1_000_000;
          const buyerClauseM = bestAttempt.terms.releaseClause / 1_000_000;
          const buyerDuration = bestAttempt.terms.termSeasons;

          // Descontar saldo al comprador
          updateClubBalance(
            bestAttempt.buyerClubId,
            -transferCostEur,
            `Pago de cláusula de rescisión por ${player.name}`,
            bestAttempt.id
          );

          // Abonar cláusula al vendedor
          updateClubBalance(
            bestAttempt.sellerClubId,
            +transferCostEur,
            `Ingreso por rescisión unilateral de cláusula de ${player.name}`,
            bestAttempt.id
          );

          // Actualizar club y contrato del jugador
          const updatedPlayer: Player = {
            ...player,
            clubId: bestAttempt.buyerClubId,
            clubName: 'Mi Club',
            isAmateur: false,
            contract: {
              salary: buyerSalaryM,
              durationYears: buyerDuration,
              releaseClause: buyerClauseM,
              acquisitionCost: transferCostEur / 1_000_000,
              startSeason: 1,
              endSeason: 1 + buyerDuration,
              signingBonus: 0,
            },
          };
          updatePlayer(updatedPlayer);
          playerMap.set(player.id, updatedPlayer);

          summary.push(`¡Fichaje por clausulazo completado! ${player.name} se incorpora a tu club.`);
        } else {
          // Ambos rechazados por el jugador
          playerRenewal.status = 'REJECTED';
          playerRenewal.resolutionAt = new Date().toISOString();
          playerRenewal.rejectionReason = renewalEval.reason || 'Condiciones de renovación insuficientes.';
          resolvedRenewalsCount++;

          for (const att of playerAttempts) {
            att.status = 'REJECTED';
            att.resolutionAt = new Date().toISOString();
            att.rejectionReason = bestAttemptEval.reason || 'Condiciones contractuales insuficientes.';
            resolvedAttemptsCount++;
          }
          summary.push(`${player.name} rechazó tanto la renovación como las ofertas de clausulazo.`);
        }
      } else if (playerAttempts.length > 0) {
        // CASO B: Solo hay clausulazos (sin renovación)
        let bestAttempt = playerAttempts[0];
        let bestEval = evaluatePlayerContractAcceptance(
          player,
          bestAttempt.terms.annualSalary / 1_000_000,
          bestAttempt.terms.termSeasons,
          bestAttempt.terms.releaseClause / 1_000_000,
          'Primera División',
          0
        );

        for (let i = 1; i < playerAttempts.length; i++) {
          const cur = playerAttempts[i];
          const curEval = evaluatePlayerContractAcceptance(
            player,
            cur.terms.annualSalary / 1_000_000,
            cur.terms.termSeasons,
            cur.terms.releaseClause / 1_000_000,
            'Primera División',
            0
          );
          if (curEval.score > bestEval.score) {
            bestAttempt = cur;
            bestEval = curEval;
          }
        }

        if (bestEval.score >= 70) {
          bestAttempt.status = 'ACCEPTED';
          bestAttempt.resolutionAt = new Date().toISOString();
          resolvedAttemptsCount++;

          for (const att of playerAttempts) {
            if (att.id !== bestAttempt.id) {
              att.status = 'REJECTED';
              att.resolutionAt = new Date().toISOString();
              att.rejectionReason = 'Superado por otra oferta de clausulazo.';
              resolvedAttemptsCount++;
            }
          }

          // Ejecutar traspaso
          const transferCostEur = bestAttempt.clauseAmount;
          const buyerSalaryM = bestAttempt.terms.annualSalary / 1_000_000;
          const buyerClauseM = bestAttempt.terms.releaseClause / 1_000_000;
          const buyerDuration = bestAttempt.terms.termSeasons;

          updateClubBalance(
            bestAttempt.buyerClubId,
            -transferCostEur,
            `Pago de cláusula de rescisión por ${player.name}`,
            bestAttempt.id
          );

          updateClubBalance(
            bestAttempt.sellerClubId,
            +transferCostEur,
            `Ingreso por rescisión unilateral de cláusula de ${player.name}`,
            bestAttempt.id
          );

          const updatedPlayer: Player = {
            ...player,
            clubId: bestAttempt.buyerClubId,
            clubName: 'Mi Club',
            isAmateur: false,
            contract: {
              salary: buyerSalaryM,
              durationYears: buyerDuration,
              releaseClause: buyerClauseM,
              acquisitionCost: transferCostEur / 1_000_000,
              startSeason: 1,
              endSeason: 1 + buyerDuration,
              signingBonus: 0,
            },
          };
          updatePlayer(updatedPlayer);
          playerMap.set(player.id, updatedPlayer);

          summary.push(`¡Clausulazo ejecutado! ${player.name} aceptó tu oferta y se une a tu plantilla.`);
        } else {
          for (const att of playerAttempts) {
            att.status = 'REJECTED';
            att.resolutionAt = new Date().toISOString();
            att.rejectionReason = bestEval.reason || 'El jugador consideró insuficiente la propuesta contractual.';
            resolvedAttemptsCount++;
          }
          summary.push(`Clausulazo denegado: ${player.name} no aceptó las condiciones ofrecidas.`);
        }
      } else if (playerRenewal) {
        // CASO C: Solo hay renovación (sin clausulazos)
        const renewalSalaryM = playerRenewal.offeredContract.annualSalary / 1_000_000;
        const renewalClauseM = playerRenewal.offeredContract.releaseClause / 1_000_000;
        const renewalDuration = playerRenewal.offeredContract.termSeasons;
        const renewalEval = evaluatePlayerContractAcceptance(
          player,
          renewalSalaryM,
          renewalDuration,
          renewalClauseM,
          'Primera División',
          0
        );

        if (renewalEval.score >= 70) {
          playerRenewal.status = 'ACCEPTED';
          playerRenewal.resolutionAt = new Date().toISOString();
          resolvedRenewalsCount++;

          const updatedPlayer: Player = {
            ...player,
            contract: {
              ...player.contract,
              salary: renewalSalaryM,
              durationYears: renewalDuration,
              releaseClause: renewalClauseM,
              startSeason: 1,
              endSeason: 1 + renewalDuration,
            },
          };
          updatePlayer(updatedPlayer);
          playerMap.set(player.id, updatedPlayer);

          summary.push(`¡Renovación confirmada! ${player.name} firma su nuevo contrato por 1 temporada.`);
        } else {
          playerRenewal.status = 'REJECTED';
          playerRenewal.resolutionAt = new Date().toISOString();
          playerRenewal.rejectionReason = renewalEval.reason || 'Propuesta salarial o cláusula no satisfactoria.';
          resolvedRenewalsCount++;
          summary.push(`${player.name} rechazó la propuesta de renovación: ${renewalEval.reason}`);
        }
      }
    }

    // Guardar colecciones actualizadas
    writeStorage(attemptsKey, attempts);
    writeStorage(renewalsKey, renewals);

    return {
      discoveredCount,
      resolvedAttemptsCount,
      resolvedRenewalsCount,
      summary,
    };
  },
};
